import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { LanguageContext } from '../LanguageSwitcher/LanguageContext'
import './articleListen.scss'

// Site language → speech language. Georgian is 'ge' on the site, 'ka' in BCP 47.
const SPEECH_LANG = { en: 'en', ge: 'ka', ru: 'ru', de: 'de', fr: 'fr', es: 'es', tr: 'tr' }
const SPEEDS = [1, 1.25, 1.5]
const WORDS_PER_MINUTE = 160

const synth = () => (typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null)

// Prefer the more natural-sounding voices when several match the language.
const pickVoice = (voices, lang) => {
  const matching = voices.filter((voice) => voice.lang && voice.lang.toLowerCase().startsWith(lang))
  if (!matching.length) return null
  const score = (voice) => (/natural|neural|online|google/i.test(voice.name) ? 2 : 0) + (voice.localService ? 0 : 1)
  return [...matching].sort((a, b) => score(b) - score(a))[0]
}

/**
 * "Listen to this article": reads the article aloud with the browser's own
 * speech engine, in the language the site is shown in. Nothing is sent
 * anywhere and there are no audio files to maintain.
 *
 * Text is read paragraph by paragraph (long single utterances get cut off in
 * some browsers), which also gives the progress bar its steps. If the device
 * has no voice for the current language — Georgian voices are often missing —
 * the player does not render at all.
 *
 * `selector` points at the element holding the article's title and text.
 */
const ArticleListen = ({ selector = '.mlrole-health' }) => {
  const { translations: t, selectedLanguage } = useContext(LanguageContext)
  const { pathname } = useLocation()
  const [voice, setVoice] = useState(null)
  const [playing, setPlaying] = useState(false)
  const [index, setIndex] = useState(0)
  const [speed, setSpeed] = useState(1)
  const [parts, setParts] = useState([])
  const indexRef = useRef(0)
  const speedRef = useRef(1)
  const runId = useRef(0)

  const lang = SPEECH_LANG[selectedLanguage] || 'en'

  // Voices load asynchronously in Chrome; listen for them.
  useEffect(() => {
    const engine = synth()
    if (!engine) return undefined
    const update = () => setVoice(pickVoice(engine.getVoices(), lang))
    update()
    engine.addEventListener?.('voiceschanged', update)
    return () => engine.removeEventListener?.('voiceschanged', update)
  }, [lang])

  // Collect the text to read once the article (and its translation) is on screen.
  useEffect(() => {
    const collect = () => {
      const root = document.querySelector(selector)
      if (!root) return
      const title = root.querySelector('h1')
      const body = [...root.querySelectorAll('.mlrole-health-txt-child-box h2, .mlrole-health-txt-child-box h3, .mlrole-health-txt-child-box p, .mlrole-health-txt-child-box li')]
      const texts = [title, ...body].filter(Boolean).map((el) => el.innerText.trim()).filter((text) => text.length > 1)
      setParts(texts)
    }
    const timer = setTimeout(collect, 300)
    return () => clearTimeout(timer)
  }, [selector, pathname, selectedLanguage])

  const stop = useCallback(() => {
    runId.current += 1
    synth()?.cancel()
    setPlaying(false)
  }, [])

  // Never keep talking after leaving the page or switching language.
  useEffect(() => {
    stop()
    indexRef.current = 0
    setIndex(0)
  }, [pathname, selectedLanguage, stop])
  useEffect(() => () => synth()?.cancel(), [])

  const speakFrom = useCallback((start) => {
    const engine = synth()
    if (!engine || !voice || !parts.length) return
    engine.cancel()
    const run = ++runId.current
    const say = (i) => {
      if (run !== runId.current) return
      if (i >= parts.length) {
        setPlaying(false)
        indexRef.current = 0
        setIndex(0)
        return
      }
      indexRef.current = i
      setIndex(i)
      const utterance = new SpeechSynthesisUtterance(parts[i])
      utterance.voice = voice
      utterance.lang = voice.lang
      utterance.rate = speedRef.current
      utterance.onend = () => say(i + 1)
      utterance.onerror = (event) => {
        if (event.error !== 'interrupted' && event.error !== 'canceled') setPlaying(false)
      }
      engine.speak(utterance)
    }
    setPlaying(true)
    say(start)
  }, [voice, parts])

  const toggle = () => (playing ? stop() : speakFrom(indexRef.current))

  const changeSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length]
    speedRef.current = next
    setSpeed(next)
    if (playing) speakFrom(indexRef.current)
  }

  const seek = (event) => {
    if (!parts.length) return
    const rect = event.currentTarget.getBoundingClientRect()
    const target = Math.min(parts.length - 1, Math.floor(((event.clientX - rect.left) / rect.width) * parts.length))
    indexRef.current = target
    setIndex(target)
    if (playing) speakFrom(target)
  }

  const onSeekKey = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    event.preventDefault()
    const target = Math.max(0, Math.min(parts.length - 1, indexRef.current + (event.key === 'ArrowRight' ? 1 : -1)))
    indexRef.current = target
    setIndex(target)
    if (playing) speakFrom(target)
  }

  if (!synth() || !voice || !parts.length) return null

  const words = parts.join(' ').split(/\s+/).length
  const minutes = Math.max(1, Math.round(words / WORDS_PER_MINUTE / speed))
  const progress = parts.length > 1 ? (index / (parts.length - 1)) * 100 : 0
  const label = t.artListenLabel || 'Listen to this article'

  return (
    <div className='article-listen' role='group' aria-label={label}>
      <button
        type='button'
        className='article-listen-play'
        onClick={toggle}
        aria-label={playing ? (t.artListenPause || 'Pause') : (t.artListenPlay || 'Play')}
      >
        {playing ? (
          <svg width='16' height='16' viewBox='0 0 16 16' aria-hidden='true' focusable='false'><rect x='3' y='2' width='3.5' height='12' rx='1' fill='currentColor' /><rect x='9.5' y='2' width='3.5' height='12' rx='1' fill='currentColor' /></svg>
        ) : (
          <svg width='16' height='16' viewBox='0 0 16 16' aria-hidden='true' focusable='false'><path d='M4 2.5v11a.8.8 0 0 0 1.2.7l9-5.5a.8.8 0 0 0 0-1.4l-9-5.5A.8.8 0 0 0 4 2.5z' fill='currentColor' /></svg>
        )}
      </button>

      <div className='article-listen-body'>
        <div className='article-listen-top'>
          <span className='article-listen-label'>{label}</span>
          <span className='article-listen-time'>{(t.artListenMin || '{n} min').replace('{n}', minutes)}</span>
        </div>
        <div
          className='article-listen-track'
          role='slider'
          tabIndex={0}
          aria-label={label}
          aria-valuemin={1}
          aria-valuemax={parts.length}
          aria-valuenow={index + 1}
          onClick={seek}
          onKeyDown={onSeekKey}
        >
          <span className='article-listen-fill' style={{ width: `${progress}%` }} />
        </div>
      </div>

      <button type='button' className='article-listen-speed' onClick={changeSpeed} aria-label={`${t.artListenSpeed || 'Speed'}: ${speed}×`}>
        {speed}×
      </button>
    </div>
  )
}

export default ArticleListen
